import LayeredAutocompleteSelect from "../../components/LayeredAutocompleteSelect";
import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ticketLifecycleApi, { HaloStatus } from "../../apis/ticketLifecycleApi";

interface TicketNoteModalProps {
  open: boolean;
  ticketId: string | number;
  ticketTitle: string;
  haloBaseUrl?: string;
  /** Pre-fill the note body instantly from a stored note. When non-empty, the
   *  on-demand generateNoteDraft fetch is skipped entirely (no loading spinner);
   *  when empty/null the modal generates a draft on open. */
  prefillNote?: string | null;
  onClose: () => void;
}

const TicketNoteModal: React.FC<TicketNoteModalProps> = ({
  open,
  ticketId,
  ticketTitle,
  prefillNote,
  onClose,
}) => {
  // Draft loading
  const [draftLoading, setDraftLoading] = React.useState(false);
  const [draftError, setDraftError] = React.useState<string | null>(null);

  // Halo statuses for "Set status" dropdown
  const [haloStatuses, setHaloStatuses] = React.useState<HaloStatus[]>([]);

  // Form fields
  const [note, setNote] = React.useState("");
  const [visibility, setVisibility] = React.useState<"private" | "public">("private");
  const [timeTaken, setTimeTaken] = React.useState<string>("");
  const [outcome, setOutcome] = React.useState("");
  const [statusId, setStatusId] = React.useState<string>("");

  // Submission state
  const [posting, setPosting] = React.useState(false);
  const [postError, setPostError] = React.useState<string | null>(null);
  const [posted, setPosted] = React.useState(false);

  // Fetch draft and note types when the modal opens.
  React.useEffect(() => {
    if (!open) return;

    // Reset form on each open.
    setNote("");
    setVisibility("private");
    setTimeTaken("");
    setOutcome("");
    setStatusId("");
    setPostError(null);
    setPosted(false);
    setDraftError(null);

    // Halo has no clean per-ticket-type status API (most types allow all
    // statuses anyway), so use the cached global active-status list.
    ticketLifecycleApi.getHaloStatusesCached().then((statuses) => {
      setHaloStatuses(statuses);
    }).catch(() => {
      setHaloStatuses([]);
    });

    // If a stored note is available, pre-fill instantly — no LLM round-trip.
    if (prefillNote) {
      setNote(prefillNote);
      return;
    }

    // No stored note: fall back to on-demand generation.
    setDraftLoading(true);
    ticketLifecycleApi
      .generateNoteDraft(String(ticketId))
      .then(({ draft }) => {
        setNote(draft);
        setDraftError(null);
      })
      .catch((err: unknown) => {
        setDraftError(err instanceof Error ? err.message : "Failed to generate draft.");
      })
      .finally(() => setDraftLoading(false));
  }, [open, ticketId, prefillNote]);

  const handlePost = async () => {
    setPosting(true);
    setPostError(null);
    try {
      await ticketLifecycleApi.postNote({
        ticketId,
        note,
        isPrivate: visibility === "private",
        timeTaken: timeTaken !== "" ? Number(timeTaken) : undefined,
        outcome: outcome.trim() || undefined,
        statusId: statusId || undefined,
      });
      setPosted(true);
      // Auto-close after a short success pause.
      setTimeout(() => {
        onClose();
      }, 900);
    } catch (err: unknown) {
      setPostError(err instanceof Error ? err.message : "Failed to post note. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={posting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "85vh" } }}
    >
      <DialogTitle>
        Add note — #{ticketId}
        {ticketTitle && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1.5, fontWeight: 400 }}
          >
            {ticketTitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ overflow: "hidden" }}>
        {/* Draft loading / error */}
        {draftLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Generating draft…
            </Typography>
          </Box>
        )}
        {draftError && !draftLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {draftError} You can still write a note manually.
          </Alert>
        )}

        {/* Note body */}
        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={8}
          fullWidth
          variant="outlined"
          disabled={draftLoading}
          InputProps={{
            sx: { fontFamily: "monospace", fontSize: 13, alignItems: "flex-start" },
          }}
          sx={{
            mb: 2,
            "& .MuiInputBase-inputMultiline": {
              maxHeight: "calc(85vh - 380px)",
              overflowY: "auto !important",
            },
          }}
        />

        {/* Visibility toggle */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Visibility
          </Typography>
          <ToggleButtonGroup
            value={visibility}
            exclusive
            onChange={(_e, val) => {
              if (val !== null) setVisibility(val as "private" | "public");
            }}
            size="small"
          >
            <ToggleButton value="private">Private</ToggleButton>
            <ToggleButton value="public">Public</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Private = internal note (hidden from the end customer). Public = visible to the customer.
          </Typography>
        </Box>

        {/* Time taken + Outcome row */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Time taken (hours)"
            type="number"
            value={timeTaken}
            onChange={(e) => setTimeTaken(e.target.value)}
            inputProps={{ min: 0, step: 0.25 }}
            size="small"
            sx={{ width: 180 }}
          />
          <TextField
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            size="small"
            fullWidth
            helperText="Short summary line shown in Halo (auto-filled from the first line if left blank)."
          />
        </Box>

        {/* Set status (optional) */}
        <LayeredAutocompleteSelect
          fullWidth={false}
          value={statusId}
          label="Set status (optional)"
          onChange={value => setStatusId(value)}
          size="small"
          sx={{ minWidth: 260 }}
          helperText="Optionally move the ticket to this status when the note is posted."
          disableClearable
          options={[{ value: "", label: "— Don't change status —" }, ...haloStatuses.map(status => ({ value: String(status.id), label: status.name }))]}
        />

        {/* Post error */}
        {postError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {postError}
          </Alert>
        )}

        {/* Success */}
        {posted && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Note posted to Halo successfully.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={posting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={posting ? <CircularProgress size={16} color="inherit" /> : <NoteAddIcon />}
          onClick={handlePost}
          disabled={posting || posted || !note.trim()}
        >
          Post to Halo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketNoteModal;
